import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Description as TermsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Terms & Conditions - Adilesson";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Read the terms and conditions of using the Adilesson driving lesson management and scheduling platform.');
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#1a237e',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <CarIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: '800', color: '#1a237e' }}>
              drv<span style={{ color: '#1976d2' }}>man</span>
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
          }}
        >
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <TermsIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Terms & Conditions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Updated: June 14, 2026
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Welcome to <strong>adilessons</strong>. Please read these Terms & Conditions carefully before using our software, application, and booking management website. By accessing or using our platform, you agree to be bound by these terms.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            By registering for an account or using any aspect of the adilessons platform, you signify your agreement to these terms. If you do not agree to all of these terms, you must not access or use our services.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            2. Account Registration and Responsibilities
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            To utilize scheduling, notifications, or booking features, you must register for an account:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>You must provide accurate, current, and complete information during registration.</li>
            <li>Instructors are responsible for adding valid student data, including verified phone numbers.</li>
            <li>You are responsible for keeping your password secure. We cannot and will not be liable for any loss or damage from your failure to maintain account security.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            3. Subscription Fees & Payments (Instructors)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            For instructors, premium features require an active subscription:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>Subscriptions are billed in advance on a recurring monthly or yearly basis depending on the selected plan.</li>
            <li>All subscription fees are processed securely via PayPal. You agree to PayPal's terms of service during billing setup.</li>
            <li>You can cancel your subscription at any time. Upon cancellation, your access to premium features will continue until the end of your current billing period.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            4. Lesson Booking & Cancellations
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            The scheduling feature facilitates appointments between students and independent instructors:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>adilessons is a marketplace software provider; we do not control, regulate, or set cancellation policies for individual instructors.</li>
            <li>Students are expected to adhere to the respective instructor's cancellation timeline (generally 24 to 48 hours notice).</li>
            <li>Failed bookings due to connectivity issues, user error, or scheduling overlaps are subject to resolution between the student and instructor directly.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            5. WhatsApp Dispatches and WhapiCell Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Our platform dispatches automated notification reminders via third-party systems:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>Instructors must pair their WhatsApp numbers responsibly using the QR Code scanner interface.</li>
            <li>Reminders are dispatched 1 day prior, at randomized times during day hours to prevent spam flagging.</li>
            <li>adilessons is not liable for messages delayed or blocked by WhatsApp's anti-spam algorithms or third-party service outages.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            6. Limitation of Liability
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            To the maximum extent permitted by law, adilessons and its creators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the services.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            7. Contact Us
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            If you have any questions or require clarification on these Terms & Conditions, please contact us at <strong>support@adilessons.com</strong>.
          </Typography>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adilessons. All rights reserved. Designed in the UK.
        </Typography>
      </Box>
    </Box>
  );
};

export default TermsPage;
